import React from 'react';
import type { Tone } from './charts/StackedBar';

/**
 * Μικρά κοινά της Αρχικής. `rich()`: μετάφραση με <b>…</b> → React (χωρίς HTML από τα δεδομένα — ό,τι
 * έρθει από τον server μένει κείμενο).
 */
export function rich(s: string): React.ReactNode {
  const parts = s.split(/(<b>[\s\S]*?<\/b>)/g).filter(Boolean);
  return parts.map((p, i) =>
    p.startsWith('<b>') && p.endsWith('</b>')
      ? <b key={i}>{p.slice(3, -4)}</b>
      : <React.Fragment key={i}>{p}</React.Fragment>,
  );
}

/** Το μεγάλο νούμερο της αριστερής στήλης μιας ενότητας + μία γραμμή από κάτω. */
export const Fig: React.FC<{ value: React.ReactNode; sub?: React.ReactNode; testId?: string }> = ({ value, sub, testId }) => (
  <div className="xh-fig" data-testid={testId}>
    <b>{value}</b>
    {sub && <span>{sub}</span>}
  </div>
);

/** Δύο-τρία μικρότερα νούμερα στη θέση του ενός (π.χ. ακυρώσεις παραγγελιών και ειδών τραπεζιών), με μία γραμμή κάτω. */
export const FigLines: React.FC<{ lines: Array<{ key: string; value: React.ReactNode; testId?: string }>; sub?: React.ReactNode; testId?: string }> = ({ lines, sub, testId }) => (
  <div className="xh-fig xh-fig-lines" data-testid={testId}>
    {lines.map(l => <b key={l.key} data-testid={l.testId}>{l.value}</b>)}
    {sub && <span>{sub}</span>}
  </div>
);

/** Το «συμπέρασμα» — μία πρόταση κάτω από το νούμερο. */
export const Note: React.FC<{ children: React.ReactNode; testId?: string }> = ({ children, testId }) => (
  <p className="xh-note" data-testid={testId}>{children}</p>
);

export const toneClass = (tone: Tone): string => `bg${tone}`;

export interface LegendItem {
  key: string;
  label: React.ReactNode;
  /** Τόνος (τετράγωνο) ή «line» (γραμμή) με κλάση χρώματος. */
  swatch: string;
  line?: boolean;
}

export const Legend: React.FC<{ items: LegendItem[] }> = ({ items }) => (
  <div className="xh-lg">
    {items.map(it => (
      <span key={it.key}>
        <span className={`xh-sw${it.line ? ' l' : ''} ${it.swatch}`} aria-hidden="true" />
        {it.label}
      </span>
    ))}
  </div>
);

/** Μικρός επιλογέας (Εισπράξεις | Παραγγελίες) κάτω από το συμπέρασμα. */
export function MetricSeg<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="xh-ctl">
      <div className="xh-seg sm" role="radiogroup" aria-label={label}>
        {options.map(o => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Γκρι «σκελετός» όσο φορτώνει η πρώτη φορά. */
export const Skel: React.FC<{ w?: number | string; h?: number; className?: string }> = ({ w = '100%', h = 12, className = '' }) => (
  <span className={`xh-skel ${className}`.trim()} style={{ width: w, height: h }} aria-hidden="true" />
);
